import React from 'react';

/**
 * LinkedInExportGuide
 * -------------------
 * A pure-CSS animated mini-preview that walks the user through exporting
 * their LinkedIn profile as a PDF. No external assets, no JS timers —
 * every layer is orchestrated via keyframes defined in index.css under
 * the `.sd-li-guide` scope. The animation loops every 8s and includes
 * a `prefers-reduced-motion` fallback that shows the final "downloaded"
 * frame statically.
 */
export default function LinkedInExportGuide() {
  return (
    <div
      className="sd-li-guide"
      data-testid="linkedin-export-guide"
      role="img"
      aria-label="Animated walkthrough: click More, select Save to PDF, download starts"
    >
      {/* Browser chrome (subtle, decorative) */}
      <div className="sd-li-guide__chrome" aria-hidden="true">
        <span className="sd-li-guide__dot" style={{ background: '#F87171' }} />
        <span className="sd-li-guide__dot" style={{ background: '#FBBF24' }} />
        <span className="sd-li-guide__dot" style={{ background: '#34D399' }} />
        <div className="sd-li-guide__urlbar">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#7A7F7A" strokeWidth="2.5">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span>linkedin.com/in/you</span>
        </div>
      </div>

      {/* LinkedIn profile mock */}
      <div className="sd-li-guide__stage" aria-hidden="true">
        {/* Cover strip */}
        <div className="sd-li-guide__cover" />

        {/* Avatar */}
        <div className="sd-li-guide__avatar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>

        {/* Name + title lines */}
        <div className="sd-li-guide__namebar">
          <div className="sd-li-guide__namebar-name" />
          <div className="sd-li-guide__namebar-title" />
        </div>

        {/* Action row */}
        <div className="sd-li-guide__actions">
          <span className="sd-li-guide__pill sd-li-guide__pill--primary">Open to</span>
          <span className="sd-li-guide__pill">Add section</span>
          <span className="sd-li-guide__pill sd-li-guide__more">
            More
            <span className="sd-li-guide__dots">⋯</span>
            <span className="sd-li-guide__click-ring" />
          </span>
        </div>

        {/* Dropdown */}
        <div className="sd-li-guide__dropdown">
          <div className="sd-li-guide__drop-row">
            <span className="sd-li-guide__drop-icon" />
            Share profile
          </div>
          <div className="sd-li-guide__drop-row sd-li-guide__drop-row--target">
            <span className="sd-li-guide__drop-icon sd-li-guide__drop-icon--pdf">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#0A66C2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </span>
            Save to PDF
            <span className="sd-li-guide__click-ring sd-li-guide__click-ring--pdf" />
          </div>
          <div className="sd-li-guide__drop-row">
            <span className="sd-li-guide__drop-icon" />
            Build a resume
          </div>
        </div>

        {/* Download toast */}
        <div className="sd-li-guide__toast">
          <span className="sd-li-guide__toast-icon">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <div className="sd-li-guide__toast-body">
            <div className="sd-li-guide__toast-title">Profile.pdf</div>
            <div className="sd-li-guide__toast-meta">Downloaded</div>
          </div>
        </div>

        {/* Cursor */}
        <div className="sd-li-guide__cursor" aria-hidden="true">
          <svg width="14" height="18" viewBox="0 0 14 18" fill="none">
            <path
              d="M1 1L13 8.2L7.6 9.5L10.4 15.4L8 16.6L5.2 10.9L1 14V1Z"
              fill="#0A0A0A"
              stroke="#FFFFFF"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {/* Caption rail (rotating) */}
      <div className="sd-li-guide__captions" aria-hidden="true">
        <div className="sd-li-guide__caption">
          <span className="sd-li-guide__caption-badge">1</span>
          Open your LinkedIn profile
        </div>
        <div className="sd-li-guide__caption">
          <span className="sd-li-guide__caption-badge">2</span>
          Click the <b>More ⋯</b> button
        </div>
        <div className="sd-li-guide__caption">
          <span className="sd-li-guide__caption-badge">3</span>
          Select <b>Save to PDF</b>
        </div>
        <div className="sd-li-guide__caption sd-li-guide__caption--done">
          <span className="sd-li-guide__caption-badge sd-li-guide__caption-badge--done">
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          Upload the PDF below
        </div>
      </div>

      {/* Progress dots */}
      <div className="sd-li-guide__progress" aria-hidden="true">
        <span /> <span /> <span /> <span />
      </div>

      <p className="sr-only">
        Animated instructions: 1. Open your LinkedIn profile. 2. Click the More button.
        3. Select Save to PDF. 4. Upload the downloaded PDF below.
      </p>
    </div>
  );
}
